/**
 * File state vocabulary (§13, §23).
 *
 * §23 requires the product to track five distinct things at once: the original,
 * the editable copy, the current editing state, the last saved version and the
 * recovery version. Conflating any two of them is how a viewer loses someone's
 * work — "save failed, and the editing copy is gone" is exactly the outcome
 * §13 forbids.
 */

/** The file as it arrived. Never written to without explicit approval (§4.10). */
export interface OriginalRef {
  /** Opaque platform handle — a path, a content:// URI, a security-scoped URL. */
  readonly handle: string;
  readonly displayName: string;
  readonly formatId: string | null;
  readonly sizeBytes: number;
  /** Modification time at open, to detect outside changes before overwriting. */
  readonly modifiedAtMs: number;
  /** How the file reached us, for the §31 information screen. */
  readonly source: 'external-app' | 'file-picker' | 'recent' | 'drag-drop' | 'command-line';
}

/** The protected copy every edit is applied to (§13 step 3). */
export interface WorkingCopyRef {
  readonly handle: string;
  readonly createdAtMs: number;
}

/** A crash-recovery snapshot (§23). */
export interface RecoveryPoint {
  readonly id: string;
  readonly handle: string;
  readonly createdAtMs: number;
  /** Document revision this snapshot captures. */
  readonly revision: number;
}

/** A file produced by Save As or by overwriting the original. */
export interface SavedRef {
  readonly handle: string;
  readonly displayName: string;
  readonly savedAtMs: number;
  readonly revision: number;
  /** True when the original was replaced rather than a new file written. */
  readonly replacedOriginal: boolean;
  /** §13 — result of reopening the exported bytes. */
  readonly validated: boolean;
}

export type SessionPhase =
  /** Default for every file (§4.7). */
  | 'read-only'
  /** Working copy and first recovery point are being created (§13 steps 3–4). */
  | 'preparing-edit'
  | 'editing'
  | 'saving'
  /** A save attempt failed. The working copy is retained (§13). */
  | 'save-failed'
  | 'closed';

export type SaveFailureReason =
  | 'storage-full'
  | 'permission-denied'
  | 'permission-expired'
  | 'name-conflict'
  | 'validation-failed'
  | 'io-error'
  | 'cancelled';

export interface DocumentSession {
  readonly id: string;
  readonly original: OriginalRef;
  readonly phase: SessionPhase;
  readonly workingCopy: WorkingCopyRef | null;
  readonly lastSaved: SavedRef | null;
  readonly recoveryPoints: readonly RecoveryPoint[];

  /** Bumped on every applied edit. The unit of "has anything changed". */
  readonly revision: number;
  /** Revision captured by the most recent successful save. */
  readonly savedRevision: number;
  /** Revision captured by the most recent recovery point. */
  readonly recoveredRevision: number;

  /** Consecutive failed save attempts, for backoff (§23 "automatic retry"). */
  readonly saveAttempts: number;
  readonly lastFailure: SaveFailureReason | null;
  /** Features that will not survive this format, shown before saving (§13). */
  readonly lossWarnings: readonly string[];
  /** Set when the assessment forbids editing regardless of format support. */
  readonly forceReadOnly: boolean;
}

/** True when there are edits not yet written to a saved file. */
export function isDirty(s: DocumentSession): boolean {
  return s.revision > s.savedRevision;
}

/** True when there are edits not yet captured in a recovery point. */
export function needsRecoveryPoint(s: DocumentSession): boolean {
  return s.revision > s.recoveredRevision;
}
